import React, { useState, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { scaleLog, scaleLinear, scaleQuantize } from 'd3-scale'
import {
  setActiveFeature,
  setHoveredFeature,
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
  setActiveDataset
} from "../../actions/ui";
import {
  selectActiveFeature,
  selectHoveredFeature,
  selectSearchedLocation,
  selectZoomToNsw,
  selectGeoJSON,
  selectActiveProject,
  selectSidebarIsOpen,
  selectCases,
  selectMaxCases,
  selectLatestDate,
  selectRiskByPostcode,
  selectMaxRisk,
  selectMinRisk,
  selectActiveDataset,
} from "../../reducers/";
import { projectShape } from "../../modules/project";
import styles from "./map.module.scss";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator } from "@deck.gl/core";
import { interpolateYlOrRd } from "d3-scale-chromatic"
import { GeoJsonLayer } from "@deck.gl/layers";
import ReactMapGl, {
  // SVGOverlay,
  Marker,
  NavigationControl,
} from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
// import Tooltip from "./tooltip";
import "mapbox-gl/dist/mapbox-gl.css";
import "./map.scss";
import { feature as topoToGeo } from "topojson-client";
import { useIsMobile } from "../../hooks/mobile.js";
import {
  SIDEBAR_WIDTH,
} from "../../modules/theme";

import Bowser from "bowser";
const bowser = Bowser.getParser(window.navigator.userAgent);
const browser = bowser.getBrowser();
const isIE11 =
  browser.version === "11.0" && browser.name === "Internet Explorer";

// ORIGINAL
// const NSW_BBOX = [
//   140.99927903600008,
//   -37.50507657099996,
//   153.63883173500005,
//   -28.157020554999974,
// ];

const NSW_BBOX = [
  140.49927903600008,
  -37.90507657099996,
  153.43883173500005,
  -27.457020554999974,
];

const SYD_BBOX = [
  150.6396, 
  -34.1399,
  151.3439, 
  -33.5780
]

const BLUE = [45, 45, 106];
const TRANSPARENT = [0, 0, 0, 1];

const Map = props => {
  const isMobile = useIsMobile();
  const initialViewport = {
    width: props.width,
    height: isMobile ? props.height*0.6 : props.height,
  };
  const {
    activeFeature,
    hoveredFeature,
    searchedLocation,
    // nonActiveProjects,
    // activeProjects,
    isSidebarOpen,
    setHoveredFeature,
    geometryConfig,
    activeProject,
    setSidebarIsOpen,
    zoomToNsw,
    cases,
    maxCases,
    latestDate,
    risk,
    maxRisk,
    minRisk,
    activeDataset
  } = props;
  

  // const [hoverIcon, setHoverIcon] = useState();
  // const [hoverCircle, setHoverCircle] = useState();
  const mapRef = useRef();

  const max = activeDataset === "occupations" ? maxRisk : maxCases;
  const min = activeDataset === "occupations" ? minRisk : 0;
  let scaleFunc;
  if (activeDataset === "occupations") {
    scaleFunc = scaleLinear()
      .domain([min, max])
      .range([0, 1])
  } else {
    scaleFunc = scaleLinear()
      .domain([0, max])
      .range([0, 1]);
  }

  const createNewViewport = React.useCallback(
    viewState => {
      const bbox = [NSW_BBOX.slice(0, 2).map((v, i) => parseFloat(v)), NSW_BBOX.slice(2, 4).map((v, i) => parseFloat(v))];
      const newViewport = new WebMercatorViewport({
        ...initialViewport,
        ...viewState,
      }).fitBounds(bbox, {
        padding: {
          top: 10,
          bottom: 10,
          left: 10,
          right: props.isMobile ? 10 : SIDEBAR_WIDTH,
        },
      });

      return newViewport;
    },
    [isSidebarOpen, activeProject, activeFeature]
  );

  const initialViewState = React.useMemo(() => {
    const initialViewState = createNewViewport({});

    return initialViewState;
  }, []);
  const [viewState, setViewState] = useState(initialViewState);

  // const projects = nonActiveProjects.concat(activeProjects);

  const { idSelector } = geometryConfig;

  const reset = React.useCallback(() => {
    setViewState(x => {
      const newViewport = createNewViewport(x);
      return {
        ...x,
        latitude: newViewport.latitude,
        longitude: newViewport.longitude,
        zoom: newViewport.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  });

  useLayoutEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap()) {
      return;
    }
    if (!activeFeature) {
      reset();
      return;
    }

    setViewState(viewState => {
      const newViewState = createNewViewport(viewState);
      return {
        latitude: newViewState.latitude,
        longitude: newViewState.longitude,
        zoom: newViewState.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [zoomToNsw]);

  useLayoutEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap()) {
      return;
    }
    if (!activeProject || !viewState) return;

    setViewState(viewState => {
      const newViewState = createNewViewport(viewState);

      // const hasZoomedInMoreThanRegion = newViewState.zoom < viewState.zoom;

      const hasZoomedInMoreThanRegion = false;

      return {
        latitude: hasZoomedInMoreThanRegion
          ? viewState.latitude
          : newViewState.latitude,
        longitude: hasZoomedInMoreThanRegion
          ? viewState.longitude
          : newViewState.longitude,
        zoom: hasZoomedInMoreThanRegion ? viewState.zoom : newViewState.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [activeFeature]);

  const setActiveFeatureIfNeeded = feature => {

    if (!feature) {
      if (activeFeature) {
        setActiveFeature(null);
      }
      return;
    }
    if (idSelector(feature) !== idSelector(activeFeature)) {
      props.setActiveFeature(feature);
    }
  };

  return (
    <ReactMapGl
      className={styles.mapContainer}
      width={initialViewport.width.toString()+"px"}
      height={initialViewport.height.toString()+"px"}
      initialViewState={initialViewState}
      viewState={viewState}
      onViewportChange={x => {
        setViewState(currentViewState => ({
          ...currentViewState,
          ...x,
        }));
      }}
      ref={mapRef}
      mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
      mapStyle={"mapbox://styles/smallmultiples/ck8xwkinl0osx1isg25q5vatv"}
      getCursor={({ isDragging }) => {
        if (hoveredFeature) {
          return "pointer";
        }
        return isDragging ? "grabbing" : "grab";
      }}
    >
      <DeckGL
        width={initialViewport.width}
        height={initialViewport.height}
        viewState={viewState}
        controller={false}
        getCursor={({ isDragging }) => {
          if (hoveredFeature) {
            return "pointer";
          }
          return isDragging ? "grabbing" : "grab";
        }}
        layers={[
          new GeoJsonLayer({
            id: "electorates",
            data: geometryConfig.geojsonUrl,
            dataTransform: data => {
              const geojson = topoToGeo(
                data,
                data.objects[geometryConfig.topologyObjectsName]
              );
              // Make sure items have a geometry
              const copyGeom = geojson.features.filter(item => item.geometry)
              const removeRandoms = { ...geojson, features: copyGeom };
              return removeRandoms;
            },
            stroked: true,
            lineWidthUnits: "pixels",
            // Increase line width if feature is hovered or active
            getLineWidth: d => {
              if (
                idSelector(d) === idSelector(hoveredFeature) ||
                idSelector(d) === idSelector(activeFeature)
              ) {
                return 2;
              } else {
                return 1;
              }
            },
            lineWidthMinPixels: 0.5,
            getFillColor: d => {
              if (activeDataset === "occupations") {

                if (risk[idSelector(d)]) {
                  let riskForPostcode = risk[idSelector(d)]['avg_risk']
                  const [redVal, greenVal, blueVal] = interpolateYlOrRd(scaleFunc(riskForPostcode)).substr(4).slice(0, -1).split(",").map(color => parseInt(color, 10));
                  return [redVal, greenVal, blueVal, 170];
                } else {
                  return TRANSPARENT;
                }

              } else {
                if (cases[idSelector(d)]) {
                  let numCases = cases[idSelector(d)].dates.length;
                  const [redVal, greenVal, blueVal] = interpolateYlOrRd(scaleFunc(numCases)).substr(4).slice(0, -1).split(",").map(color => parseInt(color, 10));
                  return [redVal, greenVal, blueVal, 170];
                } else {
                  return TRANSPARENT;
                }
              }

            },
            highlightColor: [0, 38, 101, 255 / 20],
            getLineColor: BLUE,
            pickable: true,
            updateTriggers: {
              // Make sure the map updates when a feature is hovered/active
              // So we can redraw line widths and change transparency
              // But not on IE11 as it doesn't work
              getFillColor: isIE11 ? [] : [activeFeature, hoveredFeature, activeDataset],
              getLineWidth: [activeFeature, hoveredFeature],
            },
            autoHighlight: true,
            // activeFeature is set when you click or hover
            onClick: d => {
              setSidebarIsOpen(true);
              setActiveFeatureIfNeeded(d.object);
              // setActiveProject(null);
            },
            onHover: ({ object, x, y }) => {
              setHoveredFeature(object);
            },
          }),
        ]}
      />

      {searchedLocation && (
        <Marker latitude={searchedLocation[1]} longitude={searchedLocation[0]}>
          <img
            style={{
              transform: `translate(-50%, -50%)`,
            }}
            alt="Marker icon, a star"
            src={process.env.PUBLIC_URL + "/img/star.svg"}
          />
        </Marker>
      )}
      <div className={styles.navigationControlBtns}>
        <NavigationControl
          onViewportChange={newViewState => {
            setViewState(x => ({
              ...x,
              zoom: newViewState.zoom,
              latitude: newViewState.latitude,
              longitude: newViewState.longitude,
              transitionInterpolator: new FlyToInterpolator(),
              transitionDuration: 200,
            }));
          }}
          showCompass={false}
          style={{ width: "40px" }}
        />
      </div>
      {/* <Tooltip
        item={hoverCircle || hoverIcon}
        offsetTop={Boolean(hoverIcon) ? -ICON_HEIGHT : -CIRCLE_RADIUS}
      /> */}
    </ReactMapGl>
  );
};

const featureShape = {
  type: PropTypes.string.isRequired,
  geometry: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
};
Map.propTypes = {
  activeProject: PropTypes.shape(projectShape),
  activeFeature: PropTypes.shape(featureShape),
  hoveredFeature: PropTypes.shape(featureShape),
  setActiveFeature: PropTypes.func.isRequired,
  setHoveredFeature: PropTypes.func.isRequired,
  setSidebarIsOpen: PropTypes.func.isRequired,
  searchedLocation: PropTypes.arrayOf(PropTypes.number),
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  geometryConfig: PropTypes.object.isRequired,
  zoomToNsw: PropTypes.number,
  cases: PropTypes.object,
  risk: PropTypes.object,
  maxCases: PropTypes.number.isRequired,
  maxRisk: PropTypes.number.isRequired,
  minRisk: PropTypes.number.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired,
  activeDataset: PropTypes.string,
  latestDate: PropTypes.string
};

const mapStateToProps = state => ({
  activeFeature: selectActiveFeature(state),
  hoveredFeature: selectHoveredFeature(state),
  searchedLocation: selectSearchedLocation(state),
  zoomToNsw: selectZoomToNsw(state),
  geojson: selectGeoJSON(state),
  cases: selectCases(state),
  risk: selectRiskByPostcode(state),
  maxRisk: selectMaxRisk(state),
  minRisk: selectMinRisk(state),
  maxCases: selectMaxCases(state),
  latestDate: selectLatestDate(state),
  activeProject: selectActiveProject(state),
  isSidebarOpen: selectSidebarIsOpen(state),
  activeDataset: selectActiveDataset(state),
});
const mapDispatchToProps = {
  setActiveFeature,
  setHoveredFeature,
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
  setActiveDataset,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js